import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import LazyLoad from 'react-lazyload';
import ReactDOMServer from 'react-dom/server';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import { axiosPath, path } from '../../../routing/helpers/router';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import TaskContainer from '../../../layout/screenSpecifics/Modules/CorrectiveActions/TaskList/TaskContainer';
import CorrectiveActionsSidebar from './CorrectiveActionsSidebar';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import Translator from '../../../layout/components/helpers/Translator';
import CAPaginator from '../../../layout/screenSpecifics/Modules/CorrectiveActions/CAPaginator';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import _ from 'lodash';
import * as moduleAction from "../../../store/actions/moduleAction";

export default function CorrectiveActionsTaskListScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { moduleBridgeHex, view, page, filtersHex } = useParams();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(true);
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened
  );
  const userBridgePermissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.userBridge?.permissions
  );
  const moduleData = useSelector((state) => state?.module?.[moduleBridgeHex]?.moduleData);
  const level1 = useSelector((state) => state.currentPage?.levels?.level1);
  const level2 = useSelector((state) => state.currentPage?.levels?.level2);
  const taskPages = useSelector((state) => state.currentPage?.tasks);
  const userMark = useSelector((state) => state.currentPage?.options?.userMark);
  const goToMark = useSelector((state) => state.currentPage?.goToMark);
  const [views] = useState(["my-tasks", "my-plants", "admin"]);
  const [tasks, setTasks] = useState(null);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  useEffect(() => {
    dispatch(currentPageAction.change("levels", {}, true));
  }, [filtersHex]);

  useEffect(() => {
    if (tasks && tasks?.tasksIDs?.[page - 1] === undefined) {
      navigate(
        path({
          name: "user_module_correctiveActions_task_list",
          params: {
            moduleBridgeHex,
            view,
            page: 1,
            filtersHex,
          },
        })
      );
    }
  }, [tasks]);

  // > LOAD TASKS
  const loadTasks = useCallback(
    async () =>
      axios
        .post(axiosPath("api_r_user_module_correctiveActions_task_list"), {
          moduleBridgeHex,
          view,
          filtersHex,
        })
        .then((r) => {
          if (isMounted.current) setTasks(r.data);
          dispatch(currentPageAction.change("tasks", r.data?.tasksIDs));
          dispatch(currentPageAction.change("pagination", r.data?.pagination));
          dispatch(currentPageAction.change("tasksPage", r.data?.tasksIDs));
          dispatch(currentPageAction.change("options", r.data?.options));
          setLoading(false);
        }),
    [moduleBridgeHex, view, filtersHex]
  );

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    dispatch(currentPageAction.change("tasks", undefined));
    dispatch(currentPageAction.change("pagination", undefined));
    dispatch(currentPageAction.change("tasksPage", undefined));

    if (
      views.includes(view) &&
      (view !== "admin" ||
        (view === "admin" &&
          (userBridgePermissions?.level === 0 ||
            userBridgePermissions?.level === 2)))
    ) {
      loadTasks();
    }
  }, [filtersHex, view, userBridgePermissions]);
  // < LOAD TASKS

  // > LOAD LEVELS
  const loadLevel = useCallback(
    async (level) =>
      axios.post(
        axiosPath("api_r_user_module_correctiveActions_task_loadLevel"),
        {
          moduleBridgeHex,
          taskIDs: tasks?.tasksIDs?.[page - 1],
          level,
        }
      ),
    [moduleBridgeHex, view, page, tasks]
  );

  useEffect(() => {
    if (tasks?.tasksIDs?.length > 0) {
      loadLevel(1).then((responseLevel1) => {
        dispatch(
          currentPageAction.change(
            "levels",
            {
              level1: !level1
                ? responseLevel1.data
                : { ...level1, ...responseLevel1.data },
            },
            true
          )
        );
        loadLevel(2).then((responseLevel2) => {
          dispatch(
            currentPageAction.change("levels", {
              level2: !level2
                ? responseLevel2.data
                : { ...level2, ...responseLevel2.data },
            })
          );
        });
      });
    }
  }, [tasks, page]);
  // < LOAD LEVELS

  // > GO TO MARKER
  useEffect(() => {
    if (
      _.filter(taskPages, (p) => p.includes(userMark)).length > 0 &&
      goToMark
    ) {
      dispatch(currentPageAction.change("goToMark", false, true));
      const elementPosition = _.indexOf(taskPages[page - 1], userMark);
      window.scrollTo({
        top: 168 * elementPosition + 70,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [userMark, goToMark]);
  // < GO TO MARKER

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath("api_r_user_module_correctiveActions_getModuleData"),
        "moduleData",
        {
          moduleBridgeHex,
        }
      )
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_correctiveActions_getPermissions"),
          "permissions",
          {
            moduleBridgeHex,
            subType: "permissions",
          }
        )
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_correctiveActions_getPermissions"),
          "jsonSettings",
          {
            moduleBridgeHex,
            subType: "jsonSettings",
          }
        )
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_correctiveActions_getPermissions"),
          "userBridge",
          {
            moduleBridgeHex,
            subType: "userBridge",
          }
        )
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);
  // < STORE CALLS

  if (!moduleData) return <AppNoInfo />;

  return (
    <>
      <CorrectiveActionsSidebar activeRoute="user_module_correctiveActions_task_list" />
      {views.includes(view) &&
        (view !== "admin" ||
          (view === "admin" &&
            (userBridgePermissions?.level === 0 ||
              userBridgePermissions?.level === 2))) && (
          <>
            <BreadcrumbHandler
              paths={
                moduleData
                  ? [
                      {
                        name: moduleData?.moduleName,
                        route: {
                          name: "user_module_correctiveActions_task_list",
                          params: {
                            moduleBridgeHex,
                            view: "my-tasks",
                            page: 1,
                            filtersHex: 0,
                          },
                        },
                      },
                      {
                        name: `${
                          view === "my-tasks"
                            ? ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.my-ac"
                                />
                              )
                            : view === "my-plants"
                            ? ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.my-plants"
                                />
                              )
                            : ReactDOMServer.renderToString(
                                <Translator
                                  file="module-correctiveActions"
                                  name="module.ca.manage-ca"
                                />
                              )
                        } (${
                          tasks?.pagination?.totalCount && !loading
                            ? tasks?.pagination?.totalCount
                            : "--"
                        } ${ReactDOMServer.renderToString(
                          <Translator
                            file="module-correctiveActions"
                            name="module.ca.tasks"
                          />
                        )})`,
                      },
                    ]
                  : [{ name: "..." }]
              }
            />
            <main role="main" className="col-md-12 col-lg-12 px-md-4">
              <div className={`${isSidebarOpened && "sidebar-expanded"}`}>
                <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0 overflow-auto">
                  <section className="content mb-5">
                    {filtersHex !== "0" && (
                      <div className="alert alert-info pointer">
                        <span
                          onClick={() =>
                            navigate(
                              path({
                                name: "user_module_correctiveActions_task_list",
                                params: {
                                  moduleBridgeHex,
                                  view,
                                  page: 1,
                                  filtersHex: "0",
                                },
                              })
                            )
                          }
                        >
                          <AppRemixIcon icon="delete-bin-6" className="mr-2" />
                          <Translator file="filter" name="filter.delete" />
                        </span>
                        <span
                          className="ml-4"
                          onClick={() =>
                            dispatch(
                              currentPageAction.change("auxSidebar", {
                                opened: "filters",
                              })
                            )
                          }
                        >
                          <AppRemixIcon icon="equalizer" className="mr-2" />
                          <Translator
                            file="module-legalCompliance"
                            name="show.filters"
                          />
                        </span>
                      </div>
                    )}
                    {loading ? (
                      <AppNoInfo minHeight={window.innerHeight - 60} />
                    ) : tasks?.tasksIDs &&
                      tasks?.tasksIDs?.length > 0 &&
                      tasks?.tasksIDs?.[page - 1] ? (
                      tasks?.tasksIDs?.[page - 1].map((taskID) => (
                        <LazyLoad key={taskID} height={168}>
                          <TaskContainer type="aa" taskID={taskID} />
                        </LazyLoad>
                      ))
                    ) : filtersHex === "0" ? (
                      <div className="alert alert-info">
                        <Translator
                          file="module-correctiveActions"
                          name="tasks.not.found"
                        />
                      </div>
                    ) : (
                      <div className="alert alert-info">
                        <Translator
                          file="module-correctiveActions"
                          name="tasks.not.found.with.selected.criteria"
                        />
                      </div>
                    )}
                    {tasks?.tasksIDs &&
                      tasks?.tasksIDs?.length > 0 &&
                      tasks?.tasksIDs?.[page - 1] !== undefined && (
                        <CAPaginator
                          pagination={tasks?.pagination}
                          route="user_module_correctiveActions_task_list"
                          page={page}
                          elementPages={tasks?.tasksIDs}
                          itemsPerPage={20}
                          className="mt-3"
                          params={{
                            moduleBridgeHex,
                            view,
                            page,
                            filtersHex,
                          }}
                        />
                      )}
                  </section>
                </div>
              </div>
            </main>
          </>
        )}
    </>
  );
}
